import { Reveal, Bubbles, WaveDivider } from "@/components/site/Motion";
import { Btn } from "@/components/site/Btn";
import { ArrowRight, Eye, ClipboardList, Compass, Users, PackageCheck, TrendingDown, ClipboardCheck, SlidersHorizontal } from "lucide-react";
import { WHY } from "@/lib/content";
import { ART } from "@/lib/assets";

const icons = [Eye, ClipboardList, Compass];

export default function WhyBleuSharks() {
  return (
    <>
      <section className="relative bg-gradient-to-b from-sky/30 to-cream pt-16 md:pt-24 pb-12 overflow-hidden" data-testid="why-hero">
        <Bubbles count={7} />
        <div className="max-w-5xl mx-auto px-6 md:px-10 relative">
          <Reveal>
            <p className="font-body text-sm font-bold uppercase tracking-[0.15em] text-ocean mb-4">Why BleuSharks</p>
            <h1 className="font-display font-extrabold text-4xl md:text-6xl text-navy leading-[1]">{WHY.hero}</h1>
          </Reveal>
        </div>
      </section>

      <section className="py-12 md:py-20" data-testid="why-problem">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <img src={ART.poolToShortlist} alt="A large pool of people narrowed into a small, strong shortlist" className="w-full rounded-[32px] border-2 border-navy shadow-brutal-lg bg-white" loading="lazy" />
          </Reveal>
          <Reveal delay={0.12}>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-navy">{WHY.intro}</h2>
            <div className="flex flex-wrap gap-2.5 mt-6">
              {WHY.problems.map((p) => (<span key={p} className="bg-white border-2 border-navy rounded-full px-4 py-1.5 font-body font-semibold text-sm">{p}</span>))}
            </div>
            <p className="font-body text-lg text-navy/75 mt-6">{WHY.bridge}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-sky/10" data-testid="why-reasons">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal><h2 className="font-display font-bold text-4xl md:text-5xl text-navy">Three reasons companies choose us.</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {WHY.reasons.map((r, i) => {
              const Icon = [TrendingDown, ClipboardCheck, SlidersHorizontal][i];
              const bg = ["bg-seagreen/15", "bg-yellow/25", "bg-coral/15"][i];
              return (
                <Reveal key={r.n} delay={i * 0.1}>
                  <div className={`${bg} border-2 border-navy rounded-3xl p-7 h-full shadow-brutal hover:-translate-y-2 hover:rotate-1 transition-transform duration-200`}>
                    <span className="inline-grid place-items-center w-12 h-12 rounded-full bg-white border-2 border-navy"><Icon className="w-6 h-6" /></span>
                    <h3 className="font-display font-bold text-2xl text-navy mt-4">{r.title}</h3>
                    <p className="font-body text-navy/70 mt-2">{r.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.1}>
            <p className="font-body text-lg text-navy/75 mt-8 max-w-3xl border-l-4 border-yellow pl-4">{WHY.itPath}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-navy" data-testid="why-two-ways">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <p className="font-display font-bold text-2xl md:text-3xl text-cream/90">{WHY.second}</p>
            <p className="font-display font-extrabold text-3xl md:text-5xl text-yellow mt-6 leading-[1.05]">{WHY.twoWays}</p>
            <div className="flex flex-wrap gap-4 justify-center mt-10">
              <Btn to="/professionals" variant="sky" testid="why-build-cta"><Users className="w-5 h-5" /> Build Your Team</Btn>
              <Btn to="/it-projects" variant="yellow" testid="why-project-cta"><PackageCheck className="w-5 h-5" /> Hand Us the Project</Btn>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="why-transparency">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <h2 className="font-display font-bold text-4xl md:text-5xl text-navy">{WHY.transparency.headline}</h2>
            <p className="font-body text-lg text-navy/70 mt-4 max-w-2xl">{WHY.transparency.body}</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {WHY.transparency.points.map((p, i) => {
              const Icon = icons[i];
              const bg = ["bg-sky/15", "bg-yellow/25", "bg-seagreen/15"][i];
              return (
                <Reveal key={p.title} delay={i * 0.1}>
                  <div className={`${bg} border-2 border-navy rounded-3xl p-7 h-full shadow-brutal`}>
                    <span className="inline-grid place-items-center w-12 h-12 rounded-full bg-white border-2 border-navy"><Icon className="w-6 h-6" /></span>
                    <h3 className="font-display font-bold text-2xl text-navy mt-4">{p.title}</h3>
                    <p className="font-body text-navy/70 mt-2">{p.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative bg-ocean" data-testid="why-story">
        <div className="bg-cream"><WaveDivider color="#0284C7" /></div>
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <Reveal>
            <p className="font-body text-sm font-bold uppercase tracking-[0.15em] text-yellow mb-4">{WHY.story.headline}</p>
            <p className="font-display font-bold text-2xl md:text-3xl text-cream leading-snug">{WHY.story.body}</p>
            <div className="mt-8"><Btn to="/contact" variant="yellow" testid="why-final-cta">Tell Us What You Need <ArrowRight className="w-5 h-5" /></Btn></div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
